import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import Login from './components/Login';
import Register from './components/Register';
import LandingPage from './components/LandingPage';
import PaymentResult from './components/PaymentResult';
import InstallPrompt from './components/InstallPrompt';
import MainHub from './components/MainHub';
import MasterclassesCatalog from './components/masterclasses/MasterclassesCatalog';
import Dashboard from './components/Dashboard';
import QuizView from './components/QuizView';
import TopicsView from './components/TopicsView';
import SubmoduleView from './components/SubmoduleView';
import MasterclassPlayer from './components/masterclasses/MasterclassPlayer';
import RPGFigureGallery from './components/masterclasses/RPGFigureGallery';
import FusedPresentationViewer from './components/fused_presentation/FusedPresentationViewer';
import FusionLibrary from './components/fused_presentation/FusionLibrary';
import FusionEditor from './components/fused_presentation/FusionEditor';
import AdminPanel from './components/AdminPanel';
import Leaderboard from './components/Leaderboard';
import JournalDelDia from './components/JournalDelDia';
import DuelMode from './components/DuelMode';
import SimpleDuelGame from './components/SimpleDuelGame';
import DuelNotification from './components/DuelNotification';
import EscapeRoomDashboard from './components/EscapeRoomDashboard';
import ClinicalCaseGame from './components/ClinicalCaseGame';
import RetroEscapeRoom from './components/RetroEscapeRoom';
import ImagenDXSubtemas from './components/ImagenDXSubtemas';
import ImagenDXCases from './components/ImagenDXCases';
import ImagenDXGame from './components/ImagenDXGame';
import FlashcardsDashboard from './components/FlashcardsDashboard';
import FlashcardViewer from './components/FlashcardViewer';
import PerlasDiarias from './components/PerlasDiarias';
import PerlasAdmin from './components/PerlasAdmin';
import PresentacionesDashboard from './components/PresentacionesDashboard';
import PresentacionViewer from './components/PresentacionViewer';
import { ENARMMatch } from './components/enarm-match';
import SimulacrosDashboard from './components/SimulacrosDashboard';
import SimulacroExam from './components/SimulacroExam';
import StudyPlanner from './components/StudyPlanner';
import ProfileEdit from './components/ProfileEdit';
import SupportPage from './components/SupportPage';
import LegalPage from './components/legal/LegalPage';
import RankUpModal from './components/RankUpModal';
import CardGame from './components/CardGame';
import useSSENotifications from './hooks/useSSENotifications';
import './App.css';
import './components/ui/toast-layout.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => {
    // Check for rank-up in any response that contains points_result
    const data = response.data;
    const pointsResult = data?.points_result;
    if (pointsResult?.rank_up && pointsResult?.new_rank_name) {
      window.dispatchEvent(new CustomEvent('rankUp', { 
        detail: { 
          rankName: pointsResult.new_rank_name,
          rankKey: pointsResult.current_rank?.key
        } 
      }));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Redirect "limited" users away from restricted sections (direct URL / manual nav).
// Backend also enforces this; this is the UX safety net.
const LIMITED_ALLOWED_ROUTES = ['/', '/landing', '/simulacros', '/duels', '/duel', '/juego-cartas', '/leaderboard', '/journal', '/perfil'];
function LimitedRedirect({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user || user.role !== 'limited') return;
    const path = location.pathname;
    const allowed = path === '/' || LIMITED_ALLOWED_ROUTES.some(a => a !== '/' && (path === a || path.startsWith(a + '/')));
    if (!allowed) navigate('/', { replace: true });
  }, [location.pathname, user, navigate]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [rankUpData, setRankUpData] = useState(null);

  // Initialize SSE connection when user is authenticated
  const token = localStorage.getItem('token');
  const { notifications, removeNotification, isConnected } = useSSENotifications(user?.id, token);

  // Handle new notifications - show one at a time
  useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      const latestNotification = notifications[notifications.length - 1];
      setCurrentNotification(latestNotification);
    }
  }, [notifications, currentNotification]);

  const handleNotificationClose = () => {
    if (currentNotification) {
      removeNotification(currentNotification.id);
      setCurrentNotification(null);
    }
  };

  const handleNotificationAccept = (action) => {
    console.log('Notification accepted with action:', action);
    // Additional logic can be added here if needed
  };

  // Listen for rank-up events from API responses
  useEffect(() => {
    const handleRankUp = (e) => {
      setRankUpData(e.detail);
    };
    window.addEventListener('rankUp', handleRankUp);
    return () => window.removeEventListener('rankUp', handleRankUp);
  }, []);

  // Usage-time tracking: heartbeat every 60s while app is active & visible
  useEffect(() => {
    if (!user) return;
    let lastActivity = Date.now();
    const markActive = () => { lastActivity = Date.now(); };
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, markActive, { passive: true }));
    const sendHeartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity > 120000) return; // skip if idle > 2min
      apiClient.post('/usage/heartbeat').catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => {
      clearInterval(interval);
      events.forEach(ev => window.removeEventListener(ev, markActive));
    };
  }, [user]);


  useEffect(() => {
    checkAuth();
  }, []);

  // Hide Emergent badge on mobile to avoid blocking app buttons
  useEffect(() => {
    const fixBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (!badge) return;
      if (window.innerWidth <= 768) {
        badge.style.display = 'none';
      } else {
        badge.style.display = '';
      }
    };
    fixBadge();
    window.addEventListener('resize', fixBadge);
    return () => window.removeEventListener('resize', fixBadge);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await apiClient.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      
      const userResponse = await apiClient.get('/auth/me');
      setUser(userResponse.data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle trial/subscription expired or pending activation (403 with code)
      if (error.response?.status === 403 && detail && typeof detail === 'object' && (detail.code === 'trial_expired' || detail.code === 'subscription_expired' || detail.code === 'pending_activation')) {
        return { 
          success: false, 
          trialExpired: detail,
          message: detail.message
        };
      }
      return { 
        success: false, 
        message: typeof detail === 'string' ? detail : (detail?.message || 'Error de login')
      };
    }
  };

  const register = async (fullName, email, password, profileImage = null, gender = null, universidad = null) => {
    try {
      // First register the user
      const registerResponse = await apiClient.post('/auth/register', {
        full_name: fullName,
        email,
        password,
        gender,
        universidad
      });

      // If there's a profile image and user was created, upload it using the new endpoint
      if (profileImage && registerResponse.data.user_id) {
        try {
          const formData = new FormData();
          formData.append('file', profileImage);
          
          await apiClient.post(`/auth/upload-profile-image-registration/${registerResponse.data.user_id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          console.log('Profile image uploaded successfully during registration');
        } catch (imageError) {
          console.log('Could not upload image during registration:', imageError);
          // Don't fail the registration if image upload fails
        }
      }

      return { 
        success: true, 
        message: 'Registro exitoso.',
        user_id: registerResponse.data.user_id
      };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error de registro' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-white">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Router>
        <LimitedRedirect user={user} />
        <Routes>
          <Route 
            path="/login" 
            element={
              !user ? (
                <Login onLogin={login} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/register" 
            element={
              !user ? (
                <Register onRegister={register} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/" 
            element={
              user ? (
                <MainHub user={user} onLogout={logout} onUserUpdate={setUser} />
              ) : (
                <LandingPage />
              )
            } 
          />
          <Route 
            path="/payment-result"
            element={<PaymentResult />}
          />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          {/* Public landing page — always accessible (used by "Obtener acceso completo") */}
          <Route path="/landing" element={<LandingPage />} />
          {/* Legal Pages */}
          <Route path="/terminos" element={<LegalPage pageType="terminos" />} />
          <Route path="/privacidad" element={<LegalPage pageType="privacidad" />} />
          <Route path="/descargo" element={<LegalPage pageType="descargo" />} />
          <Route path="/pagos-politica" element={<LegalPage pageType="pagos-politica" />} />
          <Route path="/propiedad-intelectual" element={<LegalPage pageType="propiedad-intelectual" />} />
          <Route 
            path="/cuestionarios" 
            element={
              user ? (
                <Dashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route path="/masterclasses" element={user ? <MasterclassesCatalog onLogout={logout} /> : <Navigate to="/login" replace />} />
          <Route path="/masterclasses/figuras" element={user ? <RPGFigureGallery /> : <Navigate to="/login" replace />} />
          <Route path="/masterclasses/modules/:module" element={user ? <MasterclassesCatalog onLogout={logout} /> : <Navigate to="/login" replace />} />
          <Route path="/masterclasses/modules/:module/:submodule" element={user ? <MasterclassesCatalog onLogout={logout} /> : <Navigate to="/login" replace />} />
          <Route path="/masterclasses/:classId" element={user ? <MasterclassPlayer /> : <Navigate to="/login" replace />} />
          <Route path="/admin/masterclasses" element={user?.is_admin ? <FusionLibrary admin /> : <Navigate to="/" replace />} />
          <Route path="/admin/masterclasses/piloto-erc" element={user?.is_admin ? <FusedPresentationViewer /> : <Navigate to="/" replace />} />
          <Route path="/admin/masterclasses/fusiones" element={<Navigate to="/admin/masterclasses" replace />} />
          <Route path="/admin/masterclasses/fusiones/:lessonId" element={user?.is_admin ? <FusionEditor /> : <Navigate to="/" replace />} />
          <Route path="/admin/masterclasses/fusiones/:lessonId/ver" element={user?.is_admin ? <FusedPresentationViewer /> : <Navigate to="/" replace />} />
          <Route path="/masterclasses/interactivas" element={<Navigate to="/masterclasses" replace />} />
          <Route path="/masterclasses/interactivas/:lessonId" element={user ? <FusedPresentationViewer publicView /> : <Navigate to="/login" replace />} />
          <Route 
            path="/admin" 
            element={
              user?.is_admin ? (
                <AdminPanel user={user} onLogout={logout} onUserUpdate={setUser} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/quiz/:quizId" 
            element={
              user ? (
                <QuizView user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/leaderboard" 
            element={
              user ? (
                <Leaderboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/journal" 
            element={
              user ? (
                <JournalDelDia user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/topics/:specialty" 
            element={
              user ? (
                <TopicsView user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/topics/:specialty/:submodule" 
            element={
              user ? (
                <TopicsView user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/duels" 
            element={
              user ? (
                <DuelMode user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/duel/:duelId" 
            element={
              user ? (
                <Navigate to="/duels" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/juego-cartas" 
            element={
              user ? (
                <CardGame user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/escape-room" 
            element={
              user ? (
                <EscapeRoomDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/escape-room/modules/:module" 
            element={
              user ? (
                <EscapeRoomDashboard user={user} onLogout={logout} showSubmodules={true} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/escape-room/modules/:module/:submodule" 
            element={
              user ? (
                <EscapeRoomDashboard user={user} onLogout={logout} showCases={true} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/escape-room/play/:caseId" 
            element={
              user ? (
                <RetroEscapeRoom user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/imagen-dx" 
            element={
              user ? (
                <ImagenDXSubtemas user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/imagen-dx/subtemas/:system" 
            element={
              user ? (
                <ImagenDXCases user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/imagen-dx/game/:caseId" 
            element={
              user ? (
                <ImagenDXGame user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/submodules/:specialty" 
            element={
              user ? (
                <SubmoduleView user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/flashcards" 
            element={
              user ? (
                <FlashcardsDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/flashcards/:tema" 
            element={
              user ? (
                <FlashcardsDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/flashcards/:tema/:subtema" 
            element={
              user ? (
                <FlashcardsDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/flashcards/deck/:tema/:subtema/:deckName" 
            element={
              user ? (
                <FlashcardViewer user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/perlas-diarias" 
            element={
              user ? (
                <PerlasDiarias user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/perlas/:pearlId" 
            element={
              user ? (
                <PerlasDiarias user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/perlas-admin" 
            element={
              user?.is_admin ? (
                <PerlasAdmin user={user} onLogout={logout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          {/* Presentaciones Routes */}
          <Route 
            path="/presentaciones" 
            element={
              user ? (
                <PresentacionesDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/presentaciones/:module" 
            element={
              user ? (
                <PresentacionesDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/presentaciones/:module/list" 
            element={
              user ? (
                <PresentacionesDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/presentaciones/:module/:submodule" 
            element={
              user ? (
                <PresentacionesDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/presentaciones/view/:presentationId" 
            element={
              user ? (
                <PresentacionViewer user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          {/* ENARM Match Routes */}
          <Route 
            path="/enarm-match" 
            element={
              user ? (
                <ENARMMatch user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          {/* Simulacros Routes */}
          <Route 
            path="/planificador" 
            element={
              user ? (
                <StudyPlanner user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/perfil" 
            element={
              user ? (
                <ProfileEdit user={user} onUserUpdate={setUser} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/soporte" 
            element={
              user ? (
                <SupportPage user={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/simulacros" 
            element={
              user ? (
                <SimulacrosDashboard user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/simulacros/:simulacroId" 
            element={
              user ? (
                <SimulacroExam user={user} onLogout={logout} />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </Router>
      
      {/* Real-time Duel Notifications */}
      {currentNotification && (
        <DuelNotification
          notification={currentNotification}
          onClose={handleNotificationClose}
          onAccept={handleNotificationAccept}
        />
      )}
      
      <Toaster position="top-right" />
      
      {/* Rank Up Modal */}
      {rankUpData && (
        <RankUpModal
          rankName={rankUpData.rankName}
          rankKey={rankUpData.rankKey}
          gender={user?.gender}
          onClose={() => setRankUpData(null)}
        />
      )}
    </div>
  );
}

// Export apiClient as 'api' for use in other components
export const api = apiClient;

export default App;